import fp from 'lodash/fp';
import React, { useState, useMemo, useRef, useCallback, useEffect, FC } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { VariableSizeGrid, GridChildComponentProps, VariableSizeGridProps } from 'react-window';

import { cn, multipleRef, useSizeContext, Heading } from 'Modules/ui';

import { getColumnMinWidth } from '../calculations';
import { GridAdapter } from '../GridAdapter';
import { cellStyles } from '../Cells';
import { DragHandleProps, HeaderItemData, HeaderProps } from './types';
import styles from './index.module.scss';


const DragHandle: FC<DragHandleProps> = ({
    columnIndex,
    isLast,
    onResize,
    onResizeFinished,
    onResizeReset,
}) => {
    const onDrag = useCallback<DraggableEventHandler>(
        (e, { deltaX }) =>
            // eslint-disable-next-line no-extra-parens
            onResize(columnIndex, deltaX, (e as React.MouseEvent<HTMLElement>).clientX),
        [columnIndex, onResize],
    );
    return (
        <Draggable
            axis='x'
            // @ts-ignore
            position={{ x: 0 }}
            onDrag={onDrag}
            onStop={onResizeFinished}
        >
            <div
                className={cn(
                    styles.dragHandle,
                    isLast && styles.last,
                )}
                onDoubleClick={onResizeReset}
            />
        </Draggable>
    );
};

const headerRenderer = React.memo<GridChildComponentProps<HeaderItemData>>(({
    style,
    columnIndex,
    data: { checkable, columns, onResize, onResizeFinished, onResizeReset, className },
}) => {
    const { Header, Cell, fixedWidth, ...column } = columns[columnIndex];
    const isLast = columnIndex === columns.length - 1;
    return (
        <Heading
            component='div'
            headingType='h3'
            className={cn(
                cellStyles.cell,
                className,
                !checkable && columnIndex === 0 && cellStyles.first,
                isLast && cellStyles.last,
            )}
            style={style}
        >
            <div className={styles.content}>
                <Header column={column} />
            </div>
            {!fixedWidth && (
                <DragHandle
                    columnIndex={columnIndex}
                    isLast={isLast}
                    onResize={onResize}
                    onResizeFinished={onResizeFinished}
                    onResizeReset={onResizeReset}
                />
            )}
            {fixedWidth && (
                <div
                    className={cn(
                        styles.dragHandle,
                        styles.fixed,
                        isLast && styles.last,
                    )}
                    onDoubleClick={onResizeReset}
                />
            )}
        </Heading>
    );
});

export const Header = React.forwardRef<VariableSizeGrid, HeaderProps>(({
    width, height, style, classNames = {},
    checkable,
    columns,
    columnWidths,
    onResizeFinished,
    onResizeReset,
}, ref) => {
    const headerRef = useRef<VariableSizeGrid>();

    useEffect(
        () => {
            headerRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
        },
        [columnWidths],
    );

    const scrollPosition = useRef(0);
    const updateScrollPosition = useCallback<VariableSizeGridProps['onScroll']>(
        ({ scrollLeft }) => {
            scrollPosition.current = scrollLeft;
        },
        [],
    );

    const { rem } = useSizeContext();
    const [draggedColumnWidths, setDraggedColumnWidths] = useState<HeaderProps['columnWidths']>(null);

    const columnWidth = useCallback<VariableSizeGridProps['columnWidth']>(
        index => Math.max(
            (draggedColumnWidths || columnWidths)[index] || 0,
            getColumnMinWidth(columns[index], rem),
        ),
        [columnWidths, columns, draggedColumnWidths, rem],
    );

    const updateColumnSize = useCallback<HeaderItemData['onResize']>(
        (index, delta, clientX) => {
            setDraggedColumnWidths(widths => {
                const [...newWidths] = widths || columnWidths;
                newWidths[index] += delta;
                return newWidths;
            });

            headerRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: index });
            document.body.style.cursor = 'col-resize';
            if (clientX > window.innerWidth - 10)
                headerRef.current.scrollTo({ scrollLeft: scrollPosition.current + delta });
        },
        [columnWidths],
    );

    const applyNewColumnSizes = useCallback<HeaderItemData['onResizeFinished']>(
        () => {
            if (!draggedColumnWidths)
                return;

            document.body.style.cursor = '';
            onResizeFinished(draggedColumnWidths, scrollPosition.current);
            setDraggedColumnWidths(null);
            headerRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
        },
        [draggedColumnWidths, onResizeFinished],
    );

    const resetResizes = useCallback<HeaderItemData['onResizeReset']>(
        () => {
            onResizeReset();
            setDraggedColumnWidths(null);
            headerRef.current.resetAfterIndices({ rowIndex: 0, columnIndex: 0 });
        },
        [onResizeReset],
    );

    return (
        <GridAdapter
            ref={multipleRef(headerRef, ref)}
            className={cn(styles.container, classNames.container)}
            width={width}
            height={height}
            style={style}

            rowCount={1}
            rowHeight={fp.constant(height)}

            columnCount={columns.length}
            columnWidth={columnWidth}

            onScroll={updateScrollPosition}

            itemData={useMemo<HeaderItemData>(
                () => ({
                    checkable,
                    columns,
                    className: classNames.cell,
                    onResize: updateColumnSize,
                    onResizeFinished: applyNewColumnSizes,
                    onResizeReset: resetResizes,
                }),
                [
                    checkable,
                    columns,
                    classNames.cell,
                    updateColumnSize,
                    applyNewColumnSizes,
                    resetResizes,
                ],
            )}
        >
            {headerRenderer}
        </GridAdapter>
    );
});
